import { allowedMime, isValidMimeType, useUploadDocument } from "@api";
import type { TJSONSchema } from "@artifact-management/json-dynamic-form";
import { DocumentTypes } from "@bbj-types/document/enum";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Button, FormControl, FormHelperText, IconButton, Stack, Typography } from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { type FieldValues, type Path, type PathValue, useController } from "react-hook-form";
import type { IFormGenInputProps } from "./index";

export type TFileReference = { id: string; url: string; name: string; mimeType: string };

const isFileReference = (value: unknown): value is TFileReference =>
  typeof value === "object" && value !== null && "name" in value && typeof value.name === "string";

const getFileReferences = (value: unknown): TFileReference[] => {
  if (Array.isArray(value)) {
    return value.filter(isFileReference);
  }
  return isFileReference(value) ? [value] : [];
};

export const FormGenInputFileUpload = <Schema extends TJSONSchema, T extends FieldValues>({
  control,
  uiSchemaElement,
  jsonSchemaElement,
  multiple,
}: IFormGenInputProps<Schema, T>) => {
  const { accept, typeDoc, isDeletable } = jsonSchemaElement.fileUpload ?? {};
  const [{ loading }, uploadDocument] = useUploadDocument();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { field, fieldState } = useController({
    control,
    name: uiSchemaElement.scope as unknown as Path<T>,
    defaultValue: (multiple ? [] : null) as PathValue<T, Path<T>>,
  });

  const handleFiles = async (fileList: FileList) => {
    const docType = (typeDoc ?? DocumentTypes.ATTACHMENT) as DocumentTypes;
    const references: TFileReference[] = [];
    const rejected: string[] = [];
    for (const file of Array.from(fileList)) {
      const mimeType = file.type;
      if (!isValidMimeType(mimeType)) {
        rejected.push(`${file.name} (format non supporté)`);
        continue;
      }
      try {
        const { url, fields, document } = (await uploadDocument(file.name, docType, mimeType, isDeletable ?? true))
          .data;
        await axios.postForm(url, { ...fields, file });
        references.push({ id: document._id, url: document.url, name: document.name, mimeType: document.mimeType });
      } catch {
        rejected.push(`${file.name} (échec de l'envoi)`);
      }
    }
    // Surface rejected files instead of dropping them silently (no request/no error was the confusing part).
    setUploadError(rejected.length ? `Fichiers ignorés : ${rejected.join(", ")}` : null);
    if (!references.length) {
      return;
    }
    if (multiple) {
      const current = Array.isArray(field.value) ? field.value.filter(isFileReference) : [];
      field.onChange([...current, ...references]);
    } else {
      field.onChange(references[0]);
    }
  };

  const removeFile = (index: number) => {
    setUploadError(null);
    if (multiple) {
      field.onChange(getFileReferences(field.value).filter((_, i) => i !== index));
    } else {
      field.onChange(null);
    }
  };

  const files = getFileReferences(field.value);
  const helperText = fieldState.error?.message ?? uploadError;

  return (
    <FormControl error={Boolean(fieldState.error) || Boolean(uploadError)}>
      <Stack spacing={0.5} alignItems="flex-start">
        <Button
          variant="outlined"
          component="label"
          size="small"
          startIcon={<AddIcon />}
          disabled={loading}
          color={fieldState.error || uploadError ? "error" : "primary"}
        >
          {uiSchemaElement.label}
          <input
            type="file"
            hidden
            accept={(accept ?? allowedMime).join(",")}
            multiple={multiple}
            onChange={(event) => {
              if (event.target.files) {
                void handleFiles(event.target.files);
              }
              event.target.value = "";
            }}
          />
        </Button>
        {files.map((file, index) => (
          <Stack
            key={`${file.id}-${file.name}`}
            direction="row"
            sx={{ alignItems: "center", gap: 0.5, maxWidth: "100%" }}
          >
            <Typography variant="body2" noWrap>
              {file.name}
            </Typography>
            <IconButton size="small" onClick={() => removeFile(index)} disabled={loading}>
              <DeleteIcon color="primary" fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </Stack>
    </FormControl>
  );
};
